import {
  toWSDate,
  WSElement,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps } from "react-router-dom";

import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useGetPayerEngagementQuery } from "../../../../query/payerEngagements/queries/useGetPayerEngagementQuery";
import { usePayerEngagementsListQuery } from "../../../../query/payerEngagements/queries/usePayerEngagementsListQuery";
import { usePayerQuery } from "../../../../query/payers/queries/usePayerQuery";
import { WSQueries } from "../../../../query/WSQuery";
import { FieldView } from "../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { selectorIsEngagementsActiveForPayer } from "../../../Payees/selectors/selectorIsEngagementsActiveForPayer";
import { selectorPayerEngagementNameTmp } from "../../../Payees/selectors/selectorPayerEngagementsTmp";
import { selectorPayerPayeeEngagementName } from "../../../Payees/selectors/selectorPayerPayeeEngagementName";
import { useRequirementInstructionsModal } from "../../../Requirements/components/RequirementsDetails/useRequirementInstructionsModal";
import { RequirementsTable } from "../../../Requirements/components/RequirementsTable";
import { selectorRequirementsFromEngagement } from "../../../Requirements/selectors/selectorRequirementsFromEngagement";
import { RequirementContextType } from "../../../Requirements/types";
import { sortRequirementsByPriority } from "../../../Requirements/utils";
import { PayerIntegrationWidget } from "../../components/IntegrationsSection/PayerIntegrationWidget";
import { usePayerEngagementHistoryResources } from "../../hooks/usePayerEngagementHistoryResources";
import { getPayerEngagementHistoryUrl, PAYERS_PATH } from "../../paths";
import { getEngagementsRequirementsCompleted } from "../../utils/engagement";
import { AlertEligibilityRequirementPending } from "../../components/AlertEligibilityRequirementPending/EligibilityRequirementPendingAlert";
import { selectorIsInstructionsModalAvailable } from "../../../Requirements/selectors/selectorIsInstructionsModalAvailable";

type EngagementDetailsSidebarProps = RouteComponentProps<{
  engagementId: string;
  payerId: string;
}>;

export const RoutePayerEngagementDetails: React.FC<
  EngagementDetailsSidebarProps
> = ({
  match: {
    params: { engagementId, payerId }
  },
  history
}) => {
  const queryEngagement = useGetPayerEngagementQuery(payerId, engagementId);
  const queryPayer = usePayerQuery(payerId);
  const queryPayerEngagements = usePayerEngagementsListQuery(payerId);
  const requirementInstructionsModal = useRequirementInstructionsModal();

  const historyResources = usePayerEngagementHistoryResources(engagementId);

  return (
    <WSPage>
      <WSQueries
        queries={{ queryEngagement, queryPayer, queryPayerEngagements }}
      >
        {({
          queryEngagementData,
          queryPayerData,
          queryPayerEngagementsData
        }) => {
          const isEngagementActiveForClient =
            selectorIsEngagementsActiveForPayer(queryPayerEngagementsData);

          const engagementName = isEngagementActiveForClient
            ? selectorPayerPayeeEngagementName(queryEngagementData, true)
            : selectorPayerEngagementNameTmp(queryEngagementData, true);

          const names = wsName({
            user: {
              email: queryPayerData.user?.email as string,
              profile: queryPayerData.user?.profile
            },
            member: queryPayerData.member
          });

          const areRequirementsCompleted =
            getEngagementsRequirementsCompleted(queryEngagementData);

          const isArchived =
            queryEngagementData.status === PayerPayeeEngagementStatus.Inactive;

          const requirements = sortRequirementsByPriority(
            selectorRequirementsFromEngagement(queryEngagementData)
          );

          return (
            <WSList>
              <WSPageToolbar
                title={engagementName}
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(queryEngagementData.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                breadcrumbs={[
                  {
                    label: "Clients",
                    onClick: () => {
                      history.push(PAYERS_PATH);
                    }
                  },
                  {
                    label: names.getResolvedName(),
                    onClick: () => {
                      history.push(PAYERS_PATH + `/${payerId}`);
                    }
                  },
                  {
                    label: engagementName || "Engagement"
                  }
                ]}
              />
              {!areRequirementsCompleted && !isArchived ? (
                <AlertEligibilityRequirementPending />
              ) : null}
              <WSGrid>
                <WSGrid.Item span={{ m: "12", l: "8" }}>
                  <WSList gap="L">
                    <WSElement>
                      <WSSectionToolbar title="Engagement information" />
                      <WSPanel>
                        <WSList gap="XL">
                          <FieldView
                            label="Engagement name"
                            value={engagementName}
                          />
                        </WSList>
                      </WSPanel>
                    </WSElement>
                    {requirements.length ? (
                      <WSElement>
                        <WSSectionToolbar title="Engagement requirements" />
                        <WSPanel p="NONE">
                          <RequirementsTable
                            contextType={RequirementContextType.Payer}
                            requirements={requirements}
                            hideActions={
                              queryEngagementData.status ===
                              PayerPayeeEngagementStatus.Inactive
                            }
                            onViewDetails={async (
                              requirement,
                              additionalData
                            ) => {
                              if (
                                selectorIsInstructionsModalAvailable(
                                  requirement,
                                  additionalData
                                )
                              ) {
                                const instructionsResult =
                                  await requirementInstructionsModal.open({
                                    requirementId: requirement.requirementId
                                  });

                                if (instructionsResult !== true) return;
                              }

                              history.push(
                                `/member/payers/${requirement.payerId}/requirement/${requirement.requirementId}`
                              );
                            }}
                          />
                        </WSPanel>
                      </WSElement>
                    ) : null}
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "12", l: "4" }}>
                  <WSList gap="L">
                    <PayerIntegrationWidget
                      isInactive={
                        queryEngagementData.status ===
                        PayerPayeeEngagementStatus.Inactive
                      }
                      payerId={queryPayerData.payerId}
                      engagementId={queryEngagementData.payerPayeeEngagementId}
                      customerId={
                        queryEngagementData?.payeeOwnedData?.integration
                          ?.quickbooks?.customerId
                      }
                      itemId={
                        queryEngagementData?.payeeOwnedData?.integration
                          ?.quickbooks?.itemId
                      }
                    />

                    <ResourceHistory
                      resources={historyResources}
                      onViewAll={() => {
                        history.push(
                          getPayerEngagementHistoryUrl(payerId, engagementId)
                        );
                      }}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </WSList>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
